// Date utility functions for consistent date formatting across the app

/**
 * Format date to DD/MM/YYYY
 * @param {string|Date} dateInput - Date string (YYYY-MM-DD) or Date object
 * @returns {string} Formatted date string DD/MM/YYYY
 */
export const formatDate = (dateInput) => {
  if (!dateInput) return '-';
  
  try {
    let date;
    if (typeof dateInput === 'string') {
      // Handle YYYY-MM-DD format
      if (dateInput.includes('-')) {
        const parts = dateInput.split('-');
        if (parts.length === 3) {
          return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
      }
      // Handle ISO date strings
      date = new Date(dateInput);
    } else {
      date = dateInput;
    }
    
    if (isNaN(date.getTime())) return dateInput;
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}/${month}/${year}`;
  } catch (e) {
    return dateInput;
  }
};

/**
 * Get today's date in YYYY-MM-DD format (for input fields)
 * Uses local timezone to ensure correct date
 * @returns {string} Today's date in YYYY-MM-DD format
 */
export const getTodayISO = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Check if a PO is overdue based on expected completion date
 * @param {string} expectedDate - Expected completion date (YYYY-MM-DD)
 * @param {string} status - PO status
 * @returns {boolean} True if overdue
 */
export const isOverdue = (expectedDate, status) => {
  if (!expectedDate) return false;
  if (status === 'Completed' || status === 'Rejected' || status === 'Cancelled') return false;
  
  const expected = new Date(expectedDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  expected.setHours(0, 0, 0, 0);
  
  return today > expected;
};

/**
 * Get days overdue
 * @param {string} expectedDate - Expected completion date (YYYY-MM-DD)
 * @returns {number} Number of days overdue (negative if not yet due)
 */
export const getDaysOverdue = (expectedDate) => {
  if (!expectedDate) return 0;
  
  const expected = new Date(expectedDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  expected.setHours(0, 0, 0, 0);
  
  const diffTime = today - expected;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
};

/**
 * Parse DD/MM/YYYY to YYYY-MM-DD for API
 * @param {string} dateStr - Date in DD/MM/YYYY format
 * @returns {string} Date in YYYY-MM-DD format
 */
export const parseToISO = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('/');
  if (parts.length === 3) {
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }
  return dateStr;
};

/**
 * Format number in Indian numeral system (e.g., Rs.1,00,000.00)
 * @param {number} amount - Amount to format
 * @param {boolean} includeSymbol - Whether to include Rs. symbol
 * @returns {string} Formatted currency string
 */
export const formatIndianCurrency = (amount, includeSymbol = true) => {
  if (amount === null || amount === undefined || isNaN(amount)) return '-';
  
  const sign = amount < 0 ? '-' : '';
  const absAmount = Math.abs(amount);
  
  // Split into integer and decimal
  const intPart = Math.floor(absAmount);
  const decPart = Math.round((absAmount - intPart) * 100);
  
  // Format integer part in Indian system
  let intStr = intPart.toString();
  if (intStr.length > 3) {
    const lastThree = intStr.slice(-3);
    let remaining = intStr.slice(0, -3);
    // Add commas every 2 digits from right
    let formatted = '';
    for (let i = remaining.length - 1, count = 0; i >= 0; i--, count++) {
      if (count > 0 && count % 2 === 0) {
        formatted = ',' + formatted;
      }
      formatted = remaining[i] + formatted;
    }
    intStr = formatted + ',' + lastThree;
  }
  
  const result = `${sign}${intStr}.${decPart.toString().padStart(2, '0')}`;
  return includeSymbol ? `Rs.${result}` : result;
};

/**
 * Format number in Indian numeral system without decimals (for quantities)
 * @param {number} num - Number to format
 * @returns {string} Formatted number string
 */
export const formatIndianNumber = (num) => {
  if (num === null || num === undefined || isNaN(num)) return '-';
  
  const sign = num < 0 ? '-' : '';
  const absNum = Math.abs(Math.floor(num));
  
  let numStr = absNum.toString();
  if (numStr.length > 3) {
    const lastThree = numStr.slice(-3);
    let remaining = numStr.slice(0, -3);
    let formatted = '';
    for (let i = remaining.length - 1, count = 0; i >= 0; i--, count++) {
      if (count > 0 && count % 2 === 0) {
        formatted = ',' + formatted;
      }
      formatted = remaining[i] + formatted;
    }
    numStr = formatted + ',' + lastThree;
  }
  
  return `${sign}${numStr}`;
};
